import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Phone, Mail, Star, Clock, AlertTriangle, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTACT_TYPES = [
  { key: 'tenant',       label: 'Tenant',          approval: false, color: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  { key: 'billing',      label: 'Billing',         approval: false, color: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  { key: 'emergency',    label: 'Emergency',       approval: true,  color: 'bg-red-500/15 text-red-300 border-red-500/30' },
  { key: 'maintenance',  label: 'Maintenance',     approval: true,  color: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
  { key: 'authorized',   label: 'Authorized',      approval: true,  color: 'bg-purple-500/15 text-purple-300 border-purple-500/30' },
  { key: 'vendor',       label: 'Vendor',          approval: true,  color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
];

const ContactsPanel = ({ propertyId, canEdit }) => {
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/pm/properties/${propertyId}/contacts`, { withCredentials: true });
      setItems(data.items || []);
      setPending(data.pending_changes || []);
    } catch (e) {
      toast.error(e.response?.data?.detail?.code || 'Failed to load contacts');
    }
    setLoading(false);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/pm/properties/${propertyId}/contacts`, { withCredentials: true });
        if (!alive) return;
        setItems(data.items || []);
        setPending(data.pending_changes || []);
      } catch (e) {
        if (alive) toast.error(e.response?.data?.detail?.code || 'Failed to load contacts');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [propertyId]);

  const save = async () => {
    if (!dialog.contact_type || !dialog.name?.trim()) {
      toast.error('Type and name required');
      return;
    }
    const payload = { ...dialog };
    if (!payload.email) delete payload.email;
    delete payload.id;
    delete payload.version;
    delete payload.archive_status;
    setBusy(true);
    try {
      if (dialog._isNew) {
        const { data } = await axios.post(`${API}/pm/properties/${propertyId}/contacts`, payload, { withCredentials: true });
        toast.success(data.status === 'pending' ? 'Submitted for NexFlow approval' : 'Contact added');
      } else {
        const { data } = await axios.put(`${API}/pm/properties/${propertyId}/contacts/${dialog.id}`, payload, { withCredentials: true });
        toast.success(data.status === 'pending' ? 'Update submitted for approval' : 'Contact updated');
      }
      setDialog(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.code || 'Save failed');
    }
    setBusy(false);
  };

  const archive = async (c) => {
    if (!window.confirm(`Remove ${c.name}?${CONTACT_TYPES.find((t) => t.key === c.contact_type)?.approval ? ' This will go through NexFlow approval.' : ''}`)) return;
    try {
      const { data } = await axios.delete(`${API}/pm/properties/${propertyId}/contacts/${c.id}`, { withCredentials: true });
      toast.success(data.status === 'pending' ? 'Removal submitted for approval' : 'Contact removed');
      load();
    } catch (e) { toast.error('Remove failed'); }
  };

  const grouped = items.reduce((acc, c) => { (acc[c.contact_type] = acc[c.contact_type] || []).push(c); return acc; }, {});

  return (
    <div data-testid="contacts-panel" className="space-y-4">
      {pending.length > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/40 rounded-md p-3 text-sm" data-testid="pending-contact-changes">
          <p className="text-yellow-300 font-medium flex items-center gap-1"><Clock className="w-4 h-4" /> {pending.length} change{pending.length === 1 ? '' : 's'} awaiting NexFlow approval</p>
        </div>
      )}
      {CONTACT_TYPES.map((t) => {
        const list = grouped[t.key] || [];
        return (
          <div key={t.key} data-testid={`contact-section-${t.key}`} className="bg-[#141414] border border-[#262626] rounded-md p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Badge className={`border ${t.color}`}>{t.label}</Badge>
                {t.approval && <span className="text-[#A3A3A3] text-xs flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> NexFlow approval required</span>}
                <span className="text-[#525252] text-xs">({list.length})</span>
              </div>
              {canEdit && (
                <Button size="sm" variant="outline" className="border-[#404040] text-white" data-testid={`add-${t.key}-btn`} onClick={() => setDialog({ _isNew: true, contact_type: t.key, name: '', email: '', phone: '', role_title: '', company: '', notes: '', is_primary: false, available_24_7: false })}>
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              )}
            </div>
            {list.length === 0 ? <p className="text-[#525252] text-xs italic">None yet.</p> : (
              <ul className="space-y-2">
                {list.map((c) => (
                  <li key={c.id} data-testid={`contact-row-${c.id}`} className="flex items-start justify-between gap-3 p-3 bg-[#0A0A0A] border border-[#262626] rounded">
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium flex items-center gap-2">
                        {c.name}
                        {c.is_primary && <Star className="w-3 h-3 text-[#D4AF37]" />}
                        {c.available_24_7 && <Badge className="bg-red-500/15 text-red-300 border border-red-500/30 text-[10px]">24/7</Badge>}
                        {c.pending_change && <Badge className="bg-yellow-500/15 text-yellow-300 border border-yellow-500/30 text-[10px]">PENDING</Badge>}
                      </p>
                      {c.role_title && <p className="text-[#A3A3A3] text-xs">{c.role_title}{c.company ? ` · ${c.company}` : ''}</p>}
                      <div className="flex items-center gap-3 text-xs text-[#A3A3A3] mt-1">
                        {c.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</span>}
                        {c.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</span>}
                      </div>
                      {c.notes && <p className="text-[#525252] text-xs mt-1 italic">&ldquo;{c.notes}&rdquo;</p>}
                    </div>
                    {canEdit && (
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="outline" className="border-[#404040] text-white h-7" data-testid={`edit-contact-${c.id}`} onClick={() => setDialog({ ...c, _isNew: false })}><Pencil className="w-3 h-3" /></Button>
                        <Button size="sm" variant="outline" className="border-red-500/50 text-red-300 hover:bg-red-500/10 h-7" data-testid={`remove-contact-${c.id}`} onClick={() => archive(c)}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}

      <Dialog open={!!dialog} onOpenChange={(o) => { if (!o) setDialog(null); }}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{dialog?._isNew ? 'Add contact' : 'Edit contact'}</DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">
              {dialog && CONTACT_TYPES.find((t) => t.key === dialog.contact_type)?.approval ? (
                <span className="flex items-center gap-1 text-yellow-300"><AlertTriangle className="w-3 h-3" /> This contact type requires NexFlow approval before going live.</span>
              ) : 'Saves immediately to the property.'}
            </DialogDescription>
          </DialogHeader>
          {dialog && (
            <div className="space-y-3">
              <div>
                <Label>Type</Label>
                <Select value={dialog.contact_type} onValueChange={(v) => setDialog({ ...dialog, contact_type: v })} disabled={!dialog._isNew}>
                  <SelectTrigger data-testid="contact-type-select" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626] text-white">
                    {CONTACT_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}{t.approval ? ' (approval)' : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Name *</Label>
                <Input data-testid="contact-name" value={dialog.name || ''} onChange={(e) => setDialog({ ...dialog, name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Phone</Label>
                  <Input data-testid="contact-phone" value={dialog.phone || ''} onChange={(e) => setDialog({ ...dialog, phone: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" data-testid="contact-email" value={dialog.email || ''} onChange={(e) => setDialog({ ...dialog, email: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Role / title</Label>
                  <Input value={dialog.role_title || ''} onChange={(e) => setDialog({ ...dialog, role_title: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
                </div>
                <div>
                  <Label>Company</Label>
                  <Input value={dialog.company || ''} onChange={(e) => setDialog({ ...dialog, company: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <label className="inline-flex items-center gap-2 text-sm text-white"><input type="checkbox" checked={!!dialog.is_primary} onChange={(e) => setDialog({ ...dialog, is_primary: e.target.checked })} /> Primary contact</label>
                <label className="inline-flex items-center gap-2 text-sm text-white"><input type="checkbox" checked={!!dialog.available_24_7} onChange={(e) => setDialog({ ...dialog, available_24_7: e.target.checked })} /> Available 24/7</label>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea rows={2} value={dialog.notes || ''} onChange={(e) => setDialog({ ...dialog, notes: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="border-[#404040] text-white" onClick={() => setDialog(null)} disabled={busy}>Cancel</Button>
            <Button data-testid="save-contact" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#B8941F]" disabled={busy} onClick={save}>{dialog?._isNew ? 'Add' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactsPanel;
